import React, { useState, useContext, useEffect, useMemo } from 'react';
import { UserType } from '../../../views/users/UserTypes';
import { FieldData, FieldOption, SchedulingBoardResourcesRowsFieldType } from 'types/FieldTypes';
import { createNewResource, createNewResourceGroup, EquipmentType, ResourceGroupType, ResourceType } from '../../../views/scheduling/SchedulingTypes';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useFetchData } from 'services/api/useFetchData';
import { v4 as uuidv4 } from 'uuid';
import FormFieldContext from '../FormFieldContext';
import Dropdown from '../basefields/Dropdown';
import SearchTextField from '../basefields/SearchTextField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

// Option of the resource dropdown, either an employee or a piece of equipment
type ResourceOptionType = {
    value: string;
    option_value: string;
    option_group: string;
    name: string;
};

// Stable params and query objects, a new object per render would refetch in a loop
const EQUIPMENT_LIST_PARAMS = { is_active: true };
const EQUIPMENT_TYPE_QUERY = { endpoint: 'get_equipmenttype_list', params: {} };

const SchedulingBoardResourcesRowsField: React.FC<SchedulingBoardResourcesRowsFieldType & { data: FieldData, viewKey: string }> = ({
    data, dropdownData, viewKey
}) => {
    const { t } = useTranslation();
    const { errorMessages, setUnsavedChanges } = useGlobalContext();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const [resourceGroups, setResourceGroups] = useState<ResourceGroupType[]>([]);
    const [users, setUsers] = useState<UserType[]>([]);

    // Track the equipment rows whose type was picked from the suggestions instead of typed
    const [selectedTypeRows, setSelectedTypeRows] = useState<Set<string>>(new Set());

    // Determine whether equipment can be scheduled in this environment
    const equipmentActive = useActiveFeature('equipment_scheduling');

    // Fetch the active equipment for the resource dropdown when the feature is active
    const { response: equipmentResponse } = useFetchData({
        apiUrl: equipmentActive ? 'get_equipment_list' : '',
        params: EQUIPMENT_LIST_PARAMS,
    });
    const equipmentList: EquipmentType[] = useMemo(() => equipmentResponse?.results || [], [equipmentResponse]);

    // Load the current data from the api
    useEffect(() => {
        // Load the current resource groups
        if (data.resource_groups && data.resource_groups.length > 0) {

            // Sort the resource groups
            const sortedResourceGroups = (data.resource_groups || []).sort(
                (a: ResourceGroupType, b: ResourceGroupType) => a.ordering - b.ordering
            ).map((resource_group: ResourceGroupType) => ({ ...resource_group }));

            // Set the resource groups
            setResourceGroups(sortedResourceGroups);

        // If there are no resource groups, create an empty group
        } else {
            const firstPosition = 1;
            const emptyResourceGroup = createNewResourceGroup(data.id, uuidv4(), firstPosition);
            setResourceGroups([emptyResourceGroup]);
        }
    }, [data]);

    // Set the users from the dropdown data
    useEffect(() => {
        setUsers(dropdownData?.user?.results || []);
    }, [dropdownData]);

    // Build the dropdown options of the planning rows, employees first and the equipment below
    const resourceOptions: ResourceOptionType[] = useMemo(() => {
        const userOptions: ResourceOptionType[] = users.map(user => ({
            value: `user:${user.user_hash}`,
            option_value: `user:${user.user_hash}`,
            option_group: t('scheduling.scheduling_board.resource_group_employees'),
            name: user.full_name ?? '',
        }));
        if (!equipmentActive) return userOptions;
        const equipmentOptions: ResourceOptionType[] = equipmentList.map(equipment => ({
            value: `equipment:${equipment.id}`,
            option_value: `equipment:${equipment.id}`,
            option_group: t('scheduling.scheduling_board.resource_group_equipment'),
            name: equipment.type_name ? `${equipment.name} (${equipment.type_name})` : equipment.name,
        }));
        return [...userOptions, ...equipmentOptions];
    }, [users, equipmentList, equipmentActive, t]);

    // Find the dropdown option of a resource row
    const findResourceOption = (resource: ResourceType): ResourceOptionType | undefined => {
        if (resource.user_hash) return resourceOptions.find(option => option.option_value === `user:${resource.user_hash}`);
        if (resource.equipment_id) return resourceOptions.find(option => option.option_value === `equipment:${resource.equipment_id}`);
        return undefined;
    };

    // Determine whether a group is the fixed equipment group
    const isEquipmentGroup = (group: ResourceGroupType): boolean => !!group.is_equipment_group;

    // Reset the ordering of the groups, the equipment group always stays last
    const normalizeGroupOrdering = (groups: ResourceGroupType[]): ResourceGroupType[] => {
        const nonDeletedGroups = groups.filter(group => !group.deleted);
        const regularGroups = nonDeletedGroups.filter(group => !isEquipmentGroup(group)).sort((a, b) => a.ordering - b.ordering);
        const equipmentGroups = nonDeletedGroups.filter(group => isEquipmentGroup(group));
        const reorderedGroups = [...regularGroups, ...equipmentGroups].map((group, index) => ({ ...group, ordering: index + 1 }));
        return [...reorderedGroups, ...groups.filter(group => group.deleted)];
    };

    // Store the groups in the state and the updated data, and flag the modal as changed
    const storeGroups = (groups: ResourceGroupType[]) => {
        setResourceGroups(groups);
        setUpdatedData((prev: any) => ({ ...prev, ['resource_groups']: groups }));
        if (viewKey) setUnsavedChanges(viewKey, true);
    };

    // Handle the type change of an equipment row, typed or picked from the suggestions
    const handleEquipmentTypeChange = (selectedItem: FieldOption | null, identifier: string) => {
        const typeName = selectedItem?.name ? String(selectedItem.name) : '';
        const pickedFromList = !!selectedItem?.id;

        // Remember whether the type was picked from the list for the field display
        setSelectedTypeRows(prev => {
            const updated = new Set(prev);
            if (pickedFromList) updated.add(identifier); else updated.delete(identifier);
            return updated;
        });

        // Copy the groups and update the type name of the row
        const updatedGroups = [...resourceGroups];
        updatedGroups.forEach(group => {
            const resourceIndex = group.resources?.findIndex(resource => resource.id?.toString() === identifier || resource.uuid === identifier);
            if (resourceIndex !== undefined && resourceIndex !== -1) {
                group.resources[resourceIndex].equipment_type_name = typeName;
            }
        });
        storeGroups(updatedGroups);
    };

    // Handle the input change
    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        fieldName: 'name' | 'remark' | 'equipment_name' | 'equipment_type_name',
        type: 'resource_group' | 'resource',
        identifier: string | undefined,
    ) => {
        // Copy the groups and find the group index
        const updatedGroups = [...resourceGroups];
        const groupIndex = updatedGroups.findIndex(group => group.id?.toString() === identifier || group.uuid === identifier);

        // Update the name of the resource group
        if (type === 'resource_group' && fieldName == 'name' && groupIndex !== -1) {
            updatedGroups[groupIndex][fieldName] = event.target.value;
        }

        // Update the remark or the equipment details of the resource
        else if (type === 'resource' && fieldName !== 'name') {

            // Loop through the groups to find the resource
            updatedGroups.forEach(group => {

                // Find the resource index
                const resourceIndex = group.resources?.findIndex(resource => resource.id?.toString() === identifier || resource.uuid === identifier);

                // Update the field
                if (resourceIndex !== undefined && resourceIndex !== -1) {
                    group.resources[resourceIndex][fieldName] = event.target.value;
                }
            });
        }

        // Update the resource groups and updated data
        storeGroups(updatedGroups);
    };

    // Handle the employee or equipment change of a planning row
    const handleDropdownChange = (selectedValue: string, identifier: string) => {

        // Copy the groups
        const updatedGroups = [...resourceGroups];

        // Split the selected value in its kind and key
        const [selectedKind, ...selectedKeyParts] = selectedValue.split(':');
        const selectedKey = selectedKeyParts.join(':');

        // Loop through the groups to find the resource
        updatedGroups.forEach(group => {

            // Find the resource index
            const resourceIndex = group.resources?.findIndex(resource => resource.id?.toString() === identifier || resource.uuid === identifier);

            // Update the user or the equipment of the row and clear the other
            if (resourceIndex !== undefined && resourceIndex !== -1) {
                const resource = group.resources[resourceIndex];
                if (selectedKind === 'equipment') {
                    const equipment = equipmentList.find(item => item.id === Number(selectedKey));
                    resource.user_hash = null;
                    resource.equipment_id = Number(selectedKey);
                    resource.equipment_name = equipment?.name ?? null;
                    resource.equipment_type_name = equipment?.type_name ?? null;
                    resource.resource_type = 'equipment';
                } else {
                    resource.user_hash = selectedKey;
                    resource.equipment_id = null;
                    resource.equipment_name = null;
                    resource.equipment_type_name = null;
                    resource.resource_type = 'user';
                }
            }
        });

        // Update the resource groups and updated data
        storeGroups(updatedGroups);
    };

    // Handle deletion
    const handleDelete = (type: 'resource_group' | 'resource', identifier: string | undefined) => {
        // Copy the groups and find the group index
        let copiedResourceGroups = [...resourceGroups];
        const groupIndex = copiedResourceGroups.findIndex(group => group.id?.toString() === identifier || group.uuid === identifier);

        // Handle deletion of a resource group
        if (type === 'resource_group' && groupIndex !== -1) {
            // Get the group from the copied resource groups
            const group = copiedResourceGroups[groupIndex];

            // Check if the group is a new group added in the frontend (it has an uuid but no id)
            if (!group.id && group.uuid) {
                // Directly remove it from the resource groups
                copiedResourceGroups = copiedResourceGroups.filter((_, index) => index !== groupIndex)
            } else {
                // Flag the backend known group as deleted without actually delete it
                copiedResourceGroups[groupIndex].deleted = true;
            }

            // Update the ordering of the groups which are not marked as deleted
            storeGroups(normalizeGroupOrdering(copiedResourceGroups));
        }

        // Handle deletion of a resource row
        if (type === 'resource') {

            // Loop through the groups to find the resource
            copiedResourceGroups = copiedResourceGroups.map(group => {

                // Find the resource index
                const resourceIndex = group.resources?.findIndex(resource => resource.id?.toString() === identifier || resource.uuid === identifier);

                if (resourceIndex !== undefined && resourceIndex !== -1) {
                    // Get the resource row from the copied resource groups
                    const resourceRow = group.resources[resourceIndex];

                    // Check if the resource row is a new row added in the frontend (it has an uuid but no id)
                    if (!resourceRow.id && resourceRow.uuid) {
                        // Directly delete the resource from the group
                        group.resources = group.resources.filter((_, index) => index !== resourceIndex);
                    } else {
                        // Flag the backend known resource as deleted without actually deleting it
                        group.resources[resourceIndex].deleted = true;

                        // A deleted row in the equipment group deactivates the equipment itself
                        if (isEquipmentGroup(group) && resourceRow.equipment_id) {
                            group.resources[resourceIndex].equipment_deactivate = true;
                        }
                    }

                    // Update the ordering of the resources which are not marked as deleted
                    const nonDeletedResources = group.resources.filter(resource => !resource.deleted);
                    const reorderedResources = nonDeletedResources.map((resource, index) => ({
                        ...resource,
                        ordering: index + 1,
                    }));

                    // Add the deleted resources to the reordered list to keep them in the updated data
                    group.resources = [...reorderedResources, ...group.resources.filter(resource => resource.deleted)];
                }

                return group;
            });

            // Update the resource groups and updated data
            storeGroups(copiedResourceGroups);
        }
    };

    // Handle ordering change
    const handleOrderingChange = (result: any) => {
        if (!result.destination) return;
        const { source, destination, type } = result;

        // Copy the groups
        const copiedResourceGroups = [...resourceGroups];

        // Handle resource group ordering change, the equipment group is not part of the draggable groups
        if (type === "resource_group") {

            // Only use the non deleted regular groups to determine the new ordering
            const nonDeletedGroups = copiedResourceGroups.filter(group => !group.deleted && !isEquipmentGroup(group));
            const [reorderedGroup] = nonDeletedGroups.splice(source.index, 1);
            nonDeletedGroups.splice(destination.index, 0, reorderedGroup);

            // Determine the ordering for the non deleted groups
            const updatedNonDeletedGroups = nonDeletedGroups.map((group, index) => ({
                ...group,
                ordering: index + 1,
            }));

            // Add the equipment group and the deleted groups to the list
            const equipmentGroups = copiedResourceGroups.filter(group => !group.deleted && isEquipmentGroup(group));
            const updatedGroups = normalizeGroupOrdering([...updatedNonDeletedGroups, ...equipmentGroups, ...copiedResourceGroups.filter(group => group.deleted)]);

            // Update the resource groups and updated data
            storeGroups(updatedGroups);
        }

        // Handle resource row ordering change within a group
        else if (type.startsWith("resource-")) {

            // Get the group identifier from the type
            const groupIdentifier = type.replace("resource-", "");

            // Find the group index
            const groupIndex = copiedResourceGroups.findIndex(group => group.id?.toString() === groupIdentifier || group.uuid === groupIdentifier);

            if (groupIndex !== -1) {
                // Get the group from the copied resource groups
                const group = copiedResourceGroups[groupIndex];

                // Only use the non deleted resources to determine the new ordering
                const nonDeletedResources = group.resources.filter(resource => !resource.deleted);
                const [movedResource] = nonDeletedResources.splice(source.index, 1);
                nonDeletedResources.splice(destination.index, 0, movedResource);

                // Determine the ordering for the non deleted resources
                const updatedNonDeletedResources = nonDeletedResources.map((resource, index) => ({
                    ...resource,
                    ordering: index + 1
                }));

                // Add the deleted resources to the list without changing their ordering
                group.resources = [...updatedNonDeletedResources, ...group.resources.filter(resource => resource.deleted)];
                copiedResourceGroups[groupIndex] = { ...group };

                // Update the resource groups and updated data
                storeGroups(copiedResourceGroups);
            }
        }
    };

    // Handle the creation of a new group, row or equipment row
    const handleAddRow = (type: 'resource_group' | 'resource' | 'equipment', groupIdentifier?: string) => {
        // Copy the current resource groups
        const copiedResourceGroups = [...resourceGroups];

        // Handle creation of a new resource group
        if (type === 'resource_group') {

            // Split groups in non deleted and deleted groups
            const nonDeletedGroups = copiedResourceGroups.filter(group => !group.deleted);
            const deletedGroups = copiedResourceGroups.filter(group => group.deleted);

            // Determine the position of the new group and create a group uuid
            const newPosition = nonDeletedGroups.length + 1;
            const newGroupUuid = uuidv4();

            // Create a new group with the first resource row
            const newGroup = createNewResourceGroup(data.id, newGroupUuid, newPosition);
            const newFirstResource = createNewResource(newGroupUuid, uuidv4(), 1);

            // Link the new first resource to the new group
            newGroup.resources.push(newFirstResource);

            // Add the new group before the equipment group and add the deleted groups afterwards
            const updatedGroups = normalizeGroupOrdering([...nonDeletedGroups, newGroup, ...deletedGroups]);

            // Update the resource groups and updated data
            storeGroups(updatedGroups);
        }

        // Handle creation of a new equipment row in the equipment group
        else if (type === 'equipment') {

            // Find the equipment group or create it as last group
            let equipmentGroup = copiedResourceGroups.find(group => !group.deleted && isEquipmentGroup(group));
            if (!equipmentGroup) {
                const nonDeletedGroups = copiedResourceGroups.filter(group => !group.deleted);
                equipmentGroup = createNewResourceGroup(data.id, uuidv4(), nonDeletedGroups.length + 1, true);
                equipmentGroup.name = t('scheduling.scheduling_board.equipment_group_name');
                copiedResourceGroups.push(equipmentGroup);
            }

            // Determine the position of the new row and create it as equipment row
            const nonDeletedRows = equipmentGroup.resources.filter(resource => !resource.deleted);
            const newRow = createNewResource(equipmentGroup.id?.toString() || equipmentGroup.uuid || '', uuidv4(), nonDeletedRows.length + 1);
            newRow.resource_type = 'equipment';
            newRow.equipment_name = '';
            newRow.equipment_type_name = '';

            // Add the new row at the end of the non deleted rows
            equipmentGroup.resources = [...nonDeletedRows, newRow, ...equipmentGroup.resources.filter(resource => resource.deleted)];

            // Update the resource groups and updated data
            storeGroups(normalizeGroupOrdering(copiedResourceGroups));
        }

        // Handle creation of a new resource
        else if (type === 'resource' && groupIdentifier) {

            // Find the group index to add the new resource
            const groupIndex = copiedResourceGroups.findIndex(group => group.id?.toString() === groupIdentifier || group.uuid === groupIdentifier);

            if (groupIndex !== -1) {
                // Split resources in non deleted and deleted rows
                const nonDeletedRows = copiedResourceGroups[groupIndex].resources.filter(resource => !resource.deleted);
                const deletedRows = copiedResourceGroups[groupIndex].resources.filter(resource => resource.deleted);

                // Determine the position of the new row and create it
                const newPosition = nonDeletedRows.length + 1;
                const newRow = createNewResource(groupIdentifier, uuidv4(), newPosition);

                // Add the new row at the end of the list with non deleted rows and add the deleted rows afterwards
                const updatedNonDeletedRows = [...nonDeletedRows, newRow];
                const updatedResources = [...updatedNonDeletedRows, ...deletedRows];

                // Overwrite the resources of the group with the new updated resources
                copiedResourceGroups[groupIndex] = {
                    ...copiedResourceGroups[groupIndex],
                    resources: updatedResources
                };

                // Update the resource groups and updated data
                storeGroups(copiedResourceGroups);
            }
        }
    };

    // Split the groups in the draggable regular groups and the fixed equipment group
    const regularGroups = resourceGroups.filter(group => !group.deleted && !isEquipmentGroup(group)).sort((a, b) => a.ordering - b.ordering);
    const equipmentGroup = equipmentActive ? resourceGroups.find(group => !group.deleted && isEquipmentGroup(group)) : undefined;
    const hasEquipmentGroup = !!equipmentGroup && equipmentGroup.resources.some(resource => !resource.deleted);

    // Render the row of a piece of equipment in the equipment group
    const renderEquipmentRow = (resource: ResourceType, index: number, groupIdentifier: string) => {
        const identifier = resource.id?.toString() || resource.uuid || '';
        const typeValue: FieldOption | null = resource.equipment_type_name
            ? { value: resource.equipment_type_name, name: resource.equipment_type_name }
            : null;
        return (
            <Draggable key={identifier} draggableId={identifier} index={index} isDragDisabled={(equipmentGroup?.resources.length ?? 0) <= 1}>
                {(provided) => (
                    <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <div className='resource-group-equipment-row'>
                            <input
                                type="text"
                                id={`equipment_name_${identifier}`}
                                name={`equipment_name_${identifier}`}
                                value={resource.equipment_name ?? ''}
                                onChange={event => handleInputChange(event, 'equipment_name', 'resource', identifier)}
                                placeholder={t('scheduling.scheduling_board.equipment_name_placeholder')}
                                autoFocus={false}
                                className={identifier && errorMessages?.resource_group?.resources?.[identifier]?.['equipment_name'] ? 'is-invalid' : undefined}
                            />
                            <SearchTextField
                                key={`equipment_type_${identifier}`}
                                type='searchtextfield'
                                name={`equipment_type_${identifier}`}
                                value={typeValue}
                                onSelect={(selectedItem) => handleEquipmentTypeChange(selectedItem, identifier)}
                                onUnlink={() => handleEquipmentTypeChange(null, identifier)}
                                placeholder='scheduling.scheduling_board.equipment_type_placeholder'
                                objectName='scheduling.scheduling_board.equipment_type_label'
                                query={EQUIPMENT_TYPE_QUERY}
                                disableAddNew={true}
                                allowTextualInput={true}
                                isTextualValue={!!resource.equipment_type_name && !selectedTypeRows.has(identifier)}
                                selectionFormat='name'
                            />
                            <input
                                type="text"
                                id={`remark_${identifier}`}
                                name={`remark_${identifier}`}
                                value={resource.remark ?? ''}
                                onChange={event => handleInputChange(event, 'remark', 'resource', identifier)}
                                placeholder={t('scheduling.scheduling_board.resource_remark_placeholder')}
                                autoFocus={false}
                            />
                            <div className='delete-icon tooltip-icon'>
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() => handleDelete('resource', identifier)} />
                                <span className="tooltip">{t('general.delete')}</span>
                            </div>
                            <div className={`order-icon tooltip-icon ${(equipmentGroup?.resources.length ?? 0) <= 1 ? 'visibility-hidden' : ''}`}>
                                <FontAwesomeIcon icon={faGripLines} />
                                <span className="tooltip">{t('general.reorder')}</span>
                            </div>
                        </div>
                    </div>
                )}
            </Draggable>
        );
    };

    return (
        <div className='rows-field'>
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing && (
                // Edit mode
                <div className='edit-mode'>
                    <DragDropContext onDragEnd={handleOrderingChange}>
                        <StrictModeDroppable droppableId="droppable-groups" type="resource_group">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {regularGroups.map((group, index) => {

                                        const groupIdentifier = group.id?.toString() || group.uuid;

                                        return (
                                            <Draggable key={groupIdentifier} draggableId={`group-${groupIdentifier}`} index={index} isDragDisabled={regularGroups.length <= 1}>
                                                {(provided) => {
                                                    return (
                                                        <div className={`resource-group-rows-field ${regularGroups.length === 1 ? 'single' : ''}`}
                                                             ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                            {regularGroups.length > 1 && (
                                                                <div className={`group-order-icon tooltip-icon ${regularGroups.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                    <FontAwesomeIcon icon={faGripLines} />
                                                                    <span className="tooltip">{t('general.reorder')}</span>
                                                                </div>
                                                            )}
                                                            <div>
                                                                {regularGroups.length > 1 && (
                                                                    <div className='resource-group-name'>
                                                                        <div className='rows-header resource-group-name-row'>
                                                                            <h6>{t('scheduling.scheduling_board.resource_group_header', { amount: index + 1 })} </h6>
                                                                        </div>
                                                                        <div className='rows-header resource-group-name-row'>
                                                                            <div className='header-item'>{t('scheduling.scheduling_board.resource_group_name_label')}</div>
                                                                        </div>
                                                                        <div className='resource-group-name-row'>
                                                                            <input
                                                                                type="text"
                                                                                id={`name_${groupIdentifier}`}
                                                                                name={`name_${groupIdentifier}`}
                                                                                value={group.name ?? ''}
                                                                                onChange={event => handleInputChange(event, 'name', 'resource_group', groupIdentifier)}
                                                                                placeholder={t('scheduling.scheduling_board.resource_group_name_placeholder')}
                                                                                autoFocus={false}
                                                                                className={groupIdentifier && errorMessages?.scheduling_board_resources?.[groupIdentifier]?.['name'] ? 'is-invalid' : undefined}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className='resource-group-resources'>
                                                                    {group.resources.length > 0 && (
                                                                        <div className='rows-header resource-group-resources-row'>
                                                                            <div className='header-item'>{t(equipmentActive ? 'scheduling.scheduling_board.resource_label' : 'scheduling.scheduling_board.resource_user_label')}</div>
                                                                            <div className='header-item'>{t('scheduling.scheduling_board.resource_remark_label')}</div>
                                                                            <div className='delete-placeholder'></div>
                                                                            <div className='drag-placeholder'></div>
                                                                        </div>
                                                                    )}
                                                                    <StrictModeDroppable droppableId={`droppable-${groupIdentifier}`} type={`resource-${groupIdentifier}`}>
                                                                        {(provided) => (
                                                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                                                                {group.resources
                                                                                    .filter(resource => !resource.deleted)
                                                                                    .sort((a, b) => a.ordering - b.ordering)
                                                                                    .map((resource, index) => {
                                                                                    const identifier = resource.id?.toString() || resource.uuid || '';
                                                                                    return (
                                                                                        <Draggable key={identifier} draggableId={identifier} index={index} isDragDisabled={group.resources.length <= 1}>
                                                                                            {(provided) => {
                                                                                                return (
                                                                                                    <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                                                        <div className='resource-group-resources-row'>
                                                                                                            <Dropdown<ResourceOptionType>
                                                                                                                options={resourceOptions}
                                                                                                                id={`user_${identifier}`}
                                                                                                                name={`user_${identifier}`}
                                                                                                                disabled_selected={t(equipmentActive ? 'scheduling.scheduling_board.resource_placeholder' : 'scheduling.scheduling_board.resource_user_placeholder')}
                                                                                                                selectedOption={findResourceOption(resource)}
                                                                                                                onChange={(selectedValue) => handleDropdownChange(selectedValue, identifier)}
                                                                                                                isInvalid={identifier && errorMessages?.resource_group?.resources?.[identifier]?.['user'] ? true : false}
                                                                                                                backendField='option_value'
                                                                                                                groupByField={equipmentActive ? 'option_group' : undefined}
                                                                                                                selectionFormat={(option) => `${option.name}`}
                                                                                                                optionFormat='name'
                                                                                                                allowNoneOption={false}
                                                                                                            />
                                                                                                            <input
                                                                                                                type="text"
                                                                                                                id={`remark_${identifier}`}
                                                                                                                name={`remark_${identifier}`}
                                                                                                                value={resource.remark ?? ''}
                                                                                                                onChange={event => handleInputChange(event, 'remark', 'resource', identifier)}
                                                                                                                placeholder={t('scheduling.scheduling_board.resource_remark_placeholder')}
                                                                                                                autoFocus={false}
                                                                                                                className={identifier && errorMessages?.resource_group?.resources?.[identifier]?.['remark'] ? 'is-invalid' : undefined}
                                                                                                            />
                                                                                                            <div className='delete-icon tooltip-icon'>
                                                                                                                <FontAwesomeIcon
                                                                                                                    icon={faTrash}
                                                                                                                    onClick={() => handleDelete('resource', identifier)} />
                                                                                                                <span className="tooltip">{t('general.delete')}</span>
                                                                                                            </div>
                                                                                                            <div className={`order-icon tooltip-icon ${group.resources.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                                                                <FontAwesomeIcon icon={faGripLines} />
                                                                                                                <span className="tooltip">{t('general.reorder')}</span>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )
                                                                                            }}
                                                                                        </Draggable>
                                                                                    )
                                                                                })}
                                                                                {provided.placeholder}
                                                                            </div>
                                                                        )}
                                                                    </StrictModeDroppable>
                                                                    <div onClick={(e) => {e.preventDefault(); handleAddRow('resource', groupIdentifier); }}
                                                                         className="add-new-button">
                                                                        {t('scheduling.scheduling_board.add_new_resource')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {regularGroups.length > 1 && (
                                                                <div className='group-delete-icon tooltip-icon'>
                                                                    <FontAwesomeIcon
                                                                        icon={faTrash}
                                                                        onClick={() => handleDelete('resource_group', groupIdentifier)} />
                                                                    <span className="tooltip">{t('general.delete')}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                }}
                                            </Draggable>
                                        )
                                    })}
                                </div>
                            )}
                        </StrictModeDroppable>

                        {/* With an equipment group the add group button sits above it, equipment always stays last */}
                        {hasEquipmentGroup && (
                            <div onClick={(e) => {e.preventDefault(); handleAddRow('resource_group'); }}
                                className="add-new-button add-group-above-equipment">
                                {t('scheduling.scheduling_board.add_new_resource_group')}
                            </div>
                        )}

                        {/* Render the fixed equipment group as last group */}
                        {hasEquipmentGroup && equipmentGroup && (() => {
                            const groupIdentifier = equipmentGroup.id?.toString() || equipmentGroup.uuid || '';
                            return (
                                <div className='resource-group-rows-field single'>
                                    <div>
                                        <div className='rows-header resource-group-equipment-title'>
                                            <h6>{t('scheduling.scheduling_board.equipment_group_name')}</h6>
                                        </div>
                                        <div className='resource-group-resources'>
                                            <div className='rows-header resource-group-equipment-row'>
                                                <div className='header-item'>{t('scheduling.scheduling_board.equipment_name_label')}</div>
                                                <div className='header-item'>{t('scheduling.scheduling_board.equipment_type_label')}</div>
                                                <div className='header-item'>{t('scheduling.scheduling_board.resource_remark_label')}</div>
                                                <div className='delete-placeholder'></div>
                                                <div className='drag-placeholder'></div>
                                            </div>
                                            <StrictModeDroppable droppableId={`droppable-${groupIdentifier}`} type={`resource-${groupIdentifier}`}>
                                                {(provided) => (
                                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                                        {equipmentGroup.resources
                                                            .filter(resource => !resource.deleted)
                                                            .sort((a, b) => a.ordering - b.ordering)
                                                            .map((resource, index) => renderEquipmentRow(resource, index, groupIdentifier))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </StrictModeDroppable>
                                            <div onClick={(e) => {e.preventDefault(); handleAddRow('equipment'); }}
                                                 className="add-new-button">
                                                {t('scheduling.scheduling_board.add_new_equipment')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </DragDropContext>
                    {/* Without an equipment group both add buttons sit at the bottom */}
                    {!hasEquipmentGroup && (
                        <>
                            <div onClick={(e) => {e.preventDefault(); handleAddRow('resource_group'); }}
                                className="add-new-button">
                                {t('scheduling.scheduling_board.add_new_resource_group')}
                            </div>
                            {equipmentActive && (
                                <div onClick={(e) => {e.preventDefault(); handleAddRow('equipment'); }}
                                    className="add-new-button add-equipment-button">
                                    {t('scheduling.scheduling_board.add_new_equipment')}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default SchedulingBoardResourcesRowsField;
